import { useEffect, useRef } from 'react'

const SCRIPT_SRC =
	'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
const SCRIPT_ID = 'cf-turnstile-script'

export const TURNSTILE_TOKEN_FIELD = 'cf-turnstile-response'
export const TURNSTILE_ACTION = 'turnstile-spin-v2'

const SITE_KEY =
	process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAAEH2h-mv1h_0kyiE'

function loadTurnstileScript() {
	if (window.turnstile) return Promise.resolve()

	const existing = document.getElementById(SCRIPT_ID)
	if (existing) {
		return new Promise((resolve, reject) => {
			existing.addEventListener('load', resolve)
			existing.addEventListener('error', reject)
		})
	}

	return new Promise((resolve, reject) => {
		const script = document.createElement('script')
		script.id = SCRIPT_ID
		script.src = SCRIPT_SRC
		script.async = true
		script.defer = true
		script.onload = resolve
		script.onerror = reject
		document.head.appendChild(script)
	})
}

// Explicit render rather than the auto-scan, because in a SPA the widget
// mounts long after Cloudflare's script has finished scanning the document
// Reports lifecycle via onStatus: 'ready' once the widget is actually rendered
// and solvable, 'unavailable' if it can never produce a token. Callers gate on
// 'ready' so a script that silently never loads does not trap the user.
export default function Turnstile({ onVerify, onStatus, resetSignal }) {
	const containerRef = useRef(null)
	const widgetIdRef = useRef(null)

	useEffect(() => {
		if (!containerRef.current) return

		let cancelled = false

		loadTurnstileScript()
			.then(() => {
				if (cancelled || !window.turnstile || !containerRef.current) return

				widgetIdRef.current = window.turnstile.render(containerRef.current, {
					sitekey: SITE_KEY,
					action: TURNSTILE_ACTION,
					callback: token => onVerify(token),
					'expired-callback': () => onVerify(''),
					// A widget that errored will never produce a token. Report it so
					// the form can fall back rather than trapping the user behind a
					// challenge that cannot be completed.
					'error-callback': () => {
						onVerify('')
						onStatus?.('unavailable')
					},
				})

				if (widgetIdRef.current !== undefined) onStatus?.('ready')
			})
			.catch(() => {
				// Script blocked by an extension, offline, or Cloudflare unreachable
				console.error('Turnstile failed to load')
				onStatus?.('unavailable')
			})

		return () => {
			cancelled = true
			if (widgetIdRef.current && window.turnstile) {
				window.turnstile.remove(widgetIdRef.current)
				widgetIdRef.current = null
			}
		}
	}, [onVerify, onStatus])

	// Tokens are single use — a rejected submit needs a fresh one, otherwise
	// the retry is refused as timeout-or-duplicate
	useEffect(() => {
		if (!resetSignal || !widgetIdRef.current || !window.turnstile) return
		window.turnstile.reset(widgetIdRef.current)
		onVerify('')
	}, [resetSignal, onVerify])

	return (
		<div
			ref={containerRef}
			className="cf-turnstile"
			data-sitekey={SITE_KEY}
			data-action={TURNSTILE_ACTION}
		/>
	)
}
